/**
 * Template context is partially conditional by design. Only `appPropertyName` (or the
 * synonymous `propertyName` key) is expected on every reservation; other fields may
 * be absent without indicating an error.
 */

function resolvePath(data, pathExpr) {
  if (!pathExpr || pathExpr.type !== 'PathExpression' || pathExpr.data) {
    return undefined;
  }
  let cur = data;
  for (const part of pathExpr.parts) {
    if (cur == null) return undefined;
    cur = cur[part];
  }
  return cur;
}

function isEmptyValue(val) {
  if (val === null || val === undefined) return true;
  if (typeof val === 'string' && val.trim() === '') return true;
  return false;
}

/**
 * Context often exposes `appPropertyName` while templates use `propertyName`. Try both.
 */
function resolveWithAppAliases(data, parts) {
  if (!parts || !parts.length) return undefined;

  const tryFirstSegments = first => {
    const variants = [first];
    if (typeof first === 'string' && first.length > 0 && !first.startsWith('app')) {
      variants.push(
        `app${first.charAt(0).toUpperCase()}${first.slice(1)}`
      );
    }
    return variants;
  };

  const first = parts[0];
  const rest = parts.slice(1);
  for (const seg of tryFirstSegments(first)) {
    const tryParts = rest.length ? [seg, ...rest] : [seg];
    const v = resolvePath(data, {
      type: 'PathExpression',
      parts: tryParts,
      data: false,
      original: tryParts.join('.'),
    });
    if (!isEmptyValue(v)) return v;
  }
  return undefined;
}

/**
 * @param {string} _templateBody — unused; kept for a stable call site
 * @param {object} templateData
 * @returns {{ missingPaths: string[] }} — at most `['appPropertyName']`
 */
export function validateTemplateBindings(_templateBody, templateData) {
  if (!templateData || typeof templateData !== 'object') {
    return { missingPaths: [] };
  }

  // Prefer `propertyName` lookup so both `propertyName` and `appPropertyName` keys are tried.
  const resolved = resolveWithAppAliases(templateData, ['propertyName']);
  if (!isEmptyValue(resolved)) {
    return { missingPaths: [] };
  }

  return { missingPaths: ['appPropertyName'] };
}
